import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ArrowLeft, Copy, Download, FileText, FileType, TrendingUp } from "lucide-react";

export default function MarketAnalysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [requisition, setRequisition] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequisitionAndMarketData();
  }, [id]);

  const fetchRequisitionAndMarketData = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}`);
      setRequisition(response.data);
      // Support both market_data (old) and competitive_intelligence (new)
      setMarketData(response.data.market_data || response.data.competitive_intelligence);
    } catch (error) {
      toast.error("Failed to load market analysis");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/market-analysis/export/json`);
      await navigator.clipboard.writeText(response.data.content);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/market-analysis/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}_market_analysis.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleExportWord = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/market-analysis/export/word`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}_market_analysis.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Word document downloaded!");
    } catch (error) {
      toast.error("Failed to export Word document");
    }
  };

  const handleExportText = async () => {
    try {
      const response = await apiClient.get(`/requisitions/${id}/market-analysis/export/text`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${requisition.title.replace(/\s+/g, '_')}_market_analysis.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Text file downloaded!");
    } catch (error) {
      toast.error("Failed to export text file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading market analysis...</p>
        </div>
      </div>
    );
  }

  if (!marketData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/requisition/${id}`)}
              data-testid="return-to-req-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Req
            </Button>
          </div>
        </header>
        <main className="max-w-5xl mx-auto px-6 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Market Analysis Available</h3>
              <p className="text-slate-600 mb-4">
                This requisition does not have market analysis data yet.
              </p>
              <Button onClick={() => navigate(`/requisition/${id}`)} data-testid="return-to-req-btn">
                Return to Req
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/requisition/${id}`)}
              data-testid="return-to-req-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Req
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Market Analysis</h1>
              <p className="text-slate-600">{requisition.title}</p>
            </div>

            {/* Export Buttons */}
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyToClipboard}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy to Clipboard</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportPDF}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export as PDF</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportWord}
                    >
                      <FileType className="w-4 h-4 mr-1" />
                      Word
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export as Word Document</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportText}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Text
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export as Text File</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Competitive Salary (New Structure) */}
          {marketData.competitive_salary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Competitive Salary Range</CardTitle>
                <p className="text-sm text-slate-600 mt-2">Salary insights and market data from multiple sources</p>
              </CardHeader>
              <CardContent>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-3xl font-bold text-blue-600 mb-4">{marketData.competitive_salary}</p>
                  {marketData.data_quality && (
                    <div className="flex items-center gap-4 text-sm mb-4">
                      <span className={`px-3 py-1 rounded-full font-semibold ${
                        marketData.data_quality.confidence === 'High' ? 'bg-green-100 text-green-700' :
                        marketData.data_quality.confidence === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {marketData.data_quality.confidence} Confidence
                      </span>
                      {marketData.data_quality.sample_count > 0 && (
                        <span className="text-slate-600">
                          {marketData.data_quality.sample_count} data points
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Salary Methodology Section */}
                {marketData.salary_methodology && marketData.salary_methodology.sample_count > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h4 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      How This Salary Was Calculated
                    </h4>
                    
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      {/* Data Sources */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h5 className="font-semibold text-slate-700 mb-2">Data Sources</h5>
                        <div className="space-y-1">
                          {marketData.salary_methodology.sources_used?.length > 0 && (
                            <p className="text-slate-600">
                              <span className="font-medium">Sources:</span> {marketData.salary_methodology.sources_used.join(', ')}
                            </p>
                          )}
                          {marketData.salary_methodology.companies_found?.length > 0 && (
                            <p className="text-slate-600">
                              <span className="font-medium">Sample Companies:</span> {marketData.salary_methodology.companies_found.slice(0, 5).join(', ')}
                              {marketData.salary_methodology.companies_found.length > 5 && ` +${marketData.salary_methodology.companies_found.length - 5} more`}
                            </p>
                          )}
                          {marketData.salary_methodology.bls_title && (
                            <p className="text-slate-600">
                              <span className="font-medium">BLS Benchmark:</span> {marketData.salary_methodology.bls_title} (${marketData.salary_methodology.bls_median?.toLocaleString()})
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Location Adjustment */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h5 className="font-semibold text-slate-700 mb-2">Location Adjustment</h5>
                        <div className="space-y-1">
                          <p className="text-slate-600">
                            <span className="font-medium">Target Location:</span> {marketData.salary_methodology.target_location}
                          </p>
                          <p className="text-slate-600">
                            <span className="font-medium">Cost of Living Index:</span> {(marketData.salary_methodology.target_col * 100).toFixed(0)}% of national avg
                          </p>
                          {marketData.salary_methodology.national_baseline_max && (
                            <p className="text-slate-600">
                              <span className="font-medium">National Baseline:</span> ${marketData.salary_methodology.national_baseline_min?.toLocaleString()} - ${marketData.salary_methodology.national_baseline_max?.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Calculation Steps */}
                    {marketData.salary_methodology.samples_by_location?.length > 0 && (
                      <div className="mt-4 bg-blue-50 rounded-lg p-4">
                        <h5 className="font-semibold text-slate-700 mb-3">Sample Location Normalization</h5>
                        <p className="text-xs text-slate-500 mb-2">
                          Raw salaries from different locations are normalized to a national baseline, then adjusted for your target location.
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-slate-600 border-b border-blue-200">
                                <th className="pb-2 pr-4">Location</th>
                                <th className="pb-2 pr-4">COL</th>
                                <th className="pb-2 pr-4">Raw Max</th>
                                <th className="pb-2">Normalized</th>
                              </tr>
                            </thead>
                            <tbody>
                              {marketData.salary_methodology.samples_by_location.slice(0, 6).map((sample, idx) => (
                                <tr key={idx} className="border-b border-blue-100 last:border-0">
                                  <td className="py-1.5 pr-4 text-slate-700">{sample.location || 'National'}</td>
                                  <td className="py-1.5 pr-4 text-slate-600">{(sample.col * 100).toFixed(0)}%</td>
                                  <td className="py-1.5 pr-4 text-slate-600">${sample.raw_max?.toLocaleString()}</td>
                                  <td className="py-1.5 text-slate-800 font-medium">${sample.normalized_max?.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {marketData.salary_methodology.samples_by_location.length > 6 && (
                            <p className="text-xs text-slate-500 mt-2">
                              +{marketData.salary_methodology.samples_by_location.length - 6} more samples
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Adjustment Summary */}
                    <div className="mt-4 bg-green-50 rounded-lg p-4">
                      <h5 className="font-semibold text-slate-700 mb-2">Final Calculation</h5>
                      <div className="text-sm text-slate-600 space-y-1">
                        <p>
                          <span className="font-medium">Method:</span> {marketData.salary_methodology.calculation_method}
                        </p>
                        <p>
                          <span className="font-medium">Experience Adjustment:</span> {marketData.salary_methodology.experience_multiplier === 1.0 ? 
                            'None (job board data includes all experience levels)' : 
                            `${marketData.salary_methodology.experience_multiplier}x for ${marketData.salary_methodology.years_experience}`}
                        </p>
                        {marketData.salary_methodology.clearance_premium_applied && (
                          <p>
                            <span className="font-medium">Clearance Premium:</span> +25% for Top Secret
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Salary Analysis (Old Structure - for backwards compatibility) */}
          {marketData.salary_analysis && marketData.salary_analysis.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Salary Analysis by Source</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {marketData.salary_analysis.map((source, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 pb-6 last:pb-0">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-xl font-bold text-slate-900">{source.source}</h4>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                          source.confidence === 'High' ? 'bg-green-100 text-green-700' :
                          source.confidence === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {source.confidence}
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-blue-600 mb-3">{source.range}</p>
                      {source.notes && (
                        <p className="text-sm text-slate-600 leading-relaxed mb-2">{source.notes}</p>
                      )}
                      {source.details && (
                        <p className="text-sm text-slate-600 leading-relaxed">{source.details}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top In-Demand Skills */}
          {marketData.in_demand_skills && marketData.in_demand_skills.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Top In-Demand Skills</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {marketData.in_demand_skills.map((skill, index) => (
                    <span key={index} className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Common Requirements */}
          {marketData.common_requirements && marketData.common_requirements.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Common Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {marketData.common_requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-blue-600 text-xl mt-0.5">•</span>
                      <span className="text-slate-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Typical Qualifications */}
          {marketData.typical_qualifications && marketData.typical_qualifications.trim() && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Typical Qualifications</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">{marketData.typical_qualifications}</p>
              </CardContent>
            </Card>
          )}

          {/* Unique Perks & Benefits */}
          {marketData.unique_perks && marketData.unique_perks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Unique Perks & Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {marketData.unique_perks.map((perk, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-green-600 text-xl mt-0.5">✓</span>
                      <span className="text-slate-700">{perk}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Data Sources */}
          {((marketData.data_quality && marketData.data_quality.sources && marketData.data_quality.sources.length > 0) ||
            (marketData.sources_scraped && marketData.sources_scraped.length > 0) || 
            (marketData.salary_analysis && marketData.salary_analysis.length > 0)) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Data Sources Used</CardTitle>
                <p className="text-sm text-slate-600 mt-2">Information aggregated from multiple trusted market data providers</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Show sources from data_quality.sources if available (new structure) */}
                  {marketData.data_quality && marketData.data_quality.sources && marketData.data_quality.sources.map((source, index) => (
                    <div 
                      key={`quality-${index}`}
                      className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200"
                    >
                      <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-emerald-900">{source}</span>
                    </div>
                  ))}
                  {/* Show sources_scraped if available (old structure) */}
                  {(!marketData.data_quality || !marketData.data_quality.sources) && marketData.sources_scraped && marketData.sources_scraped.map((source, index) => (
                    <div 
                      key={`scraped-${index}`}
                      className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200"
                    >
                      <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-emerald-900">{source}</span>
                    </div>
                  ))}
                  {/* Show sources from salary_analysis if no other sources available (old structure) */}
                  {(!marketData.data_quality || !marketData.data_quality.sources) &&
                   (!marketData.sources_scraped || marketData.sources_scraped.length === 0) && 
                   marketData.salary_analysis && marketData.salary_analysis.map((source, index) => (
                    <div 
                      key={`salary-${index}`}
                      className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200"
                    >
                      <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-emerald-900">{source.source}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Market Insights */}
          {marketData.insights && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Market Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 text-base leading-relaxed whitespace-pre-wrap">{marketData.insights}</p>
              </CardContent>
            </Card>
          )}

          {/* Market Trends */}
          {marketData.trends && marketData.trends.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Market Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {marketData.trends.map((trend, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-blue-600 text-xl mt-0.5">•</span>
                      <span className="text-slate-700 text-base">{trend}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Raw Market Data Text (if available) */}
          {marketData.raw_text && (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Complete Market Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap font-sans">
                  {marketData.raw_text}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Note: "Additional Market Data" section removed to prevent raw JSON display */}
          {/* All competitive intelligence fields are now displayed in proper sections above */}
        </div>
      </main>
    </div>
  );
}
